import * as Sentry from "@sentry/browser";
import { Button, Checkbox, Col, Descriptions, message, Row, Spin } from "antd";
import { CheckboxValueType } from "antd/lib/checkbox/Group";
import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import moment from "moment";
import React, { Fragment, RefObject } from "react";
import { RouteComponentProps } from "react-router";
import AttachmentsSection from "../components/AttachmentsSection";
import Card from "../components/Card";
import BaseForm from "../components/Form/BaseForm";
import CustomerBalanceBreakdown from "../components/CustomerBalanceBreakdown";
import ModalCreator from "../components/ModalCreator";
import {
  ValidateAccount,
  Activity,
  AssociatedEmail,
  AssociatedPhone,
  Attachment,
  BranchMap,
  Category,
  Company,
  Customer,
  CustomerLien,
  Department,
  FinancialInstitution,
  FormFieldConfig,
  Id,
  PaymentAccount,
  PaymentAccountTypesEnum,
  PaymentModeTypesEnum,
  PayoutVerificationFailure,
  Product,
  ProductContribution,
  ProductWithSubscriptionId,
  RedemptionRequestChecklist,
  RedemptionsRequest,
  SelectOptionsInterface,
  Subscription,
  User,
} from "../helpers/shared-interfaces";
import {
  capitalizeFirstLetter,
  convertCustomersToSelectOptions,
  displayRedemptionError,
  filterAccountsByMode,
  formatPayoutAccountAsSelectOptions,
  formatSnakeCase,
  formatter,
  generateContactTypes,
  generateNameFromCustomer,
  generateRedemptionTypes,
  getPayoutVerificationError,
  handleValidationErrors,
  injectKey,
  isAuthorizerOptionalForCompany,
  isProductDurationTenYearsOrMore,
  paymentAccountTypes,
  paymentModeTypes,
  request,
  responseHandler,
  savedPayoutAccountHiddenFields,
  filterActiveSubscriptions,
} from "../helpers/utilities";
import {
  CREATE_ATTACHMENT,
  GET_CATEGORIES,
  GET_FINANCIAL_INSTITUTIONS,
  GET_ID_TYPES,
} from "../gql/general";
import { GET_DEPARTMENTS } from "../gql/departments";
import {
  CREATE_REDEMPTION_REQUEST,
  GET_REDEMPTION_CHECKLIST,
  GET_REDEMPTION_PURPOSES,
  GET_REDEMPTION_SUB_PURPOSES,
  SEARCH_REDEMPTION_REQUEST_FOR_EDIT,
} from "../gql/redemption-request";
import {
  SEARCH_CUSTOMER_MORE_INFO,
} from "../gql/customers";
import { ALL_USERS_QUERY } from "../gql/users";
import {
  UPDATE_PAYOUT_ACCOUNT,
  VALIDATE_PAYOUT_ACCOUNT_FUZZY_MATCH,
} from "../gql/accounts";
import { ghanaCardRegex, PAYOUT_VALIDATION_THRESHOLD } from "../helpers/constants";
import { DEBOUNCE_TIME } from "../helpers/constants";
import { generateDisplayErrorMessage } from "../helpers/utilities";



interface State {
  isLoading: boolean;
  products: Product[];
  customerSubscriptions?: Subscription[];
  companySubscriptions?: Subscription[];
  customerPayoutAccounts?: PaymentAccount[];
  companyPayoutAccounts?: PaymentAccount[];
  redemptionPurposes: SelectOptionsInterface[];
  redemptionRequestSubPurposes: SelectOptionsInterface[];
  companies: Company[];
  customers: Customer[];
  redemptionRequestChecklist: RedemptionRequestChecklist[];
  searchTerm: string;
  isSearching: boolean;
  iscreateRedemptionBtnDisabled: boolean;
  selectedChecklist: string[];
  redemptionType: string;
  attachmentIds: string[];
  attachments: Attachment[];
  selectedCustomer?: Customer;
  selectedCompany?: Company;
  initiator: string;
  contactType: string;
  redemptionFieldsToHide: string[];
  contactTypeFieldsToHide: string[];
  vestingRuleFieldsToHide: string[];
  authorizerFieldsToHide: string[];
  taxWaiverFieldsToHide: string[];
  vestingRuleSectionFieldsToHide: string[];
  productsWithSubscriptionId: ProductWithSubscriptionId[];
  selectedProduct?: ProductWithSubscriptionId;
  redemptionsRequests: RedemptionsRequest[];
  showConfirmCreateModal: boolean;
  customerContributions?: ProductContribution[];
  isLoadingContributions: boolean;
  productContribution: ProductContribution | undefined;
  financialInstitutions: FinancialInstitution[];
  branchMap: BranchMap;
  creatingNewPayoutAccount: boolean;
  payoutAccountFieldsToHide: string[];
  payoutFinancialInstitutionId: string;
  paymentAccountType: string;
  idTypes: Id[];
  bankBranchId: string;

  category: Category | undefined;
  users: User[];
  initialTotalAmountType: string | undefined;
  initialRequestType: string | undefined;
  initialCompanyId: string | undefined;
  emailValue: string | undefined;
  showCancelRequest: boolean;
  userSearchTerm: string;
  isFormerCompany: boolean;
  savePayoutAccount: boolean;
  accountNumber: string
  accountName: string
  accountValidationResponse?: ValidateAccount
  institutionOptions: FinancialInstitution[];
  payoutBranches: SelectOptionsInterface[];
  customerModifiedAccounts?: SelectOptionsInterface[];
  taxWaiverNpraUrl?: string
  vestingRuleUrl?: string
  isCheque: boolean
  clientContactInformation?: string
  requestorTypeFieldsToHide: string[];
  bypassValidation: boolean
  isOlderThanSixty?: boolean;
  contributedLongerThanTenYears?: boolean;
  paymentMode?: string;
  selectedPayoutAccount?: string;
  lastVerificationFailure?: PayoutVerificationFailure;
}

interface RouteParams {
  id?: string;
}

export interface Props extends RouteComponentProps<RouteParams> {
  name?: string;
  user: User;
  customerActivityId?: string;
  companyInteractionId?: string;
  customer?: Customer;
  company?: Company;
  companyInteraction?: Activity;
  customerInteraction?: Activity;
  handleSuccess?: () => void;
}

const fieldsToBeValidated = [
  "requestDate",
  "valueDate",
  "redemptionType",
  "customerId",
  "companyId",
  "redemptionRequestPurposeId",
  "redemptionRequestSubPurposeId",
  "redemptionValue",
  "customerSubscriptionId",
  "payoutAccountId",
  "contactType",
  "totalAmountType",
  // "includeVestingRule", // option temporarily removed from the UI for Tier 3 redemptions
  "authorizerCompanyContactId",
  "vestingValue",
  "email",
  "phone",
  "idNumber",
  "waiveOffTax",
  "taxWaiverNpraUrl",
  "accountType",
  "paymentMode",
  "name",
  "accountNumber",
  "bankBranchId",
  "subCategoryId",
  "userId",
  "additionalPurposeDescription",
  "isFormerCompany",
  "savePayoutAccount",
  "financialInstitutionId",
  "clientContactInformation"
];

export const otherRedemptionPurposeId = 7;

export default class CreateRedemptionRequest extends React.Component<
  Props,
  State
> {
  reference: RefObject<FormInstance>;
  attachmentsReference: RefObject<FormInstance>;
  checklistReference: RefObject<FormInstance>;

  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: true,
      products: [],
      customerSubscriptions: [],
      customerPayoutAccounts: [],
      companySubscriptions: [],
      companies: [],
      customers: [],
      redemptionPurposes: [],
      redemptionRequestSubPurposes: [],
      redemptionRequestChecklist: [],
      searchTerm: "",
      isSearching: false,
      iscreateRedemptionBtnDisabled: true,
      selectedChecklist: [],
      redemptionFieldsToHide: [
        "redemptionRequestSubPurposeId",
        "additionalPurposeDescription",
      ],
      contactTypeFieldsToHide: ["phone", "email"],
      vestingRuleSectionFieldsToHide: ["includeVestingRule"],
      vestingRuleFieldsToHide: [
        "vestingValue",
      ],
      authorizerFieldsToHide: ["authorizerCompanyContactId"],
      taxWaiverFieldsToHide: ["taxWaiverNpraUrl"],
      contactType: "",
      redemptionType: "",
      initiator: "",
      selectedCustomer: undefined,
      attachmentIds: [],
      attachments: [],
      productsWithSubscriptionId: [],
      selectedProduct: undefined,
      redemptionsRequests: [],
      showConfirmCreateModal: false,
      customerContributions: [],
      isLoadingContributions: false,
      productContribution: undefined,
      financialInstitutions: [],
      branchMap: {},
      creatingNewPayoutAccount: false,
      payoutAccountFieldsToHide: [
        "accountType",
        "name",
        "financialInstitutionId",
        "accountNumber",
        "bankAccountType",
        "bankBranchId",
        "name"
      ],
      payoutFinancialInstitutionId: "",
      paymentAccountType: "",
      idTypes: [],
      bankBranchId: "",
      category: undefined,
      users: [],
      initialTotalAmountType: undefined,
      initialRequestType: undefined,
      initialCompanyId: undefined,
      emailValue: undefined,
      showCancelRequest: false,
      userSearchTerm: "",
      isFormerCompany: false,
      savePayoutAccount: false,
      accountNumber: "",
      institutionOptions: [],
      payoutBranches: [],
      accountName: "",
      isCheque: false,
      requestorTypeFieldsToHide: ["clientContactInformation"],
      bypassValidation: false,
      paymentMode: undefined,
      selectedPayoutAccount: undefined,
      lastVerificationFailure: undefined,
    };

    this.reference = React.createRef<FormInstance>();
    this.attachmentsReference = React.createRef<FormInstance>();
    this.checklistReference = React.createRef<FormInstance>();
  }

  componentDidMount(): void {
    if (this.props.customer || this.props.company) {
      this.setInitialSelectedCustomerOrCompany();
    }
    this.getFinancialInstitutionals();
    this.getIdTypes();
    this.getCategories();
    this.getAllUsers();
    this.getDefaultInitiator();
  }


  getDefaultInitiator = (): void => {
    let entity = "";
    let requestorTypeFieldsToHide: string[] = [];
    if (this.props.customer) {
      entity = "customer";
      requestorTypeFieldsToHide = ["clientContactInformation"];
    } else if (this.props.company) {
      entity = "company";
    }

    this.setState({ initiator: entity, requestorTypeFieldsToHide });
    this.reference.current?.setFieldsValue({ initiator: entity });
  };

  handleAccountNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const accountNumber = event.target.value;

    this.setState({ accountNumber });

    if (accountNumber.trim() === "") {
      this.setState({ isCheque: true });
    } else {
      this.setState({ isCheque: false });
    }
  };

  handleUserSearchInput = async (userSearchTerm: string): Promise<void> => {
    this.setState({ userSearchTerm }, () => {
      this.triggerUserDelayedSearch();
    });
  };

  triggerUserDelayedSearch = debounce(async (): Promise<void> => {
    this.getAllUsers();
  }, DEBOUNCE_TIME);

  toggleModal = (): void => {
    this.setState({
      showConfirmCreateModal: false,
    });
  };

  toggleSavePayoutAccount = (checkedValue: boolean) => {
    this.setState({
      savePayoutAccount: checkedValue,
    });
  };

  toggleConfirmCreateModal = (): void => {
    const { redemptionsRequests, selectedProduct } = this.state;

    const pendingRedemption = redemptionsRequests.find(
      (redemptionsRequest: RedemptionsRequest): boolean => {
        return (
          redemptionsRequest.state === "pending" &&
          redemptionsRequest.customerSubscription?.id ===
          selectedProduct?.subscriptionId
        );
      },
    );

    if (pendingRedemption) {
      this.setState({
        showConfirmCreateModal: true,
        isLoading: false,
      });
    } else {
      this.createRedemptionRequest();
    }
  };

  getRedemptionPurposes = (): void => {
    const { selectedProduct } = this.state;
    interface Response {
      data: {
        redemptionRequestPurposes: SelectOptionsInterface[];
      };
    }
    this.setState({ isSearching: true });
    request({
      query: GET_REDEMPTION_PURPOSES,
      variables: {
        productId: selectedProduct?.id,
      },
    })
      .then(({ data }: AxiosResponse<Response>): void => {
        this.setState({
          redemptionPurposes: data.data.redemptionRequestPurposes,
        });
      })
      .catch((error) => {
        Sentry.captureException(error);
      })
      .finally(() => {
        this.setState({ isSearching: false });
      });
  };

  getRequestDate = (): moment.Moment | undefined => {
    let requestDate;
    if (this.props.customerInteraction) {
      requestDate = moment(this.props.customerInteraction.activityDate);
    } else if (this.props.companyInteraction) {
      requestDate = moment(this.props.companyInteraction.activityDate);
    }

    return requestDate;
  };

  setRef = (
    name: "reference" | "attachmentsReference",
    ref: React.RefObject<FormInstance>,
  ): void => {
    this[name] = ref;
  };

  redirectToViewRedemptionPage = (id: string): void => {
    this.props.history.push(`/redemption-requests/${id}`);
  };

  handleSearchInput = async (searchTerm: string): Promise<void> => {
    this.setState({ searchTerm }, async () => {
      await this.triggerDelayedSearch();
    });
  };

  handleEmailInput = async (emailValue: string): Promise<void> => {
    this.setState({ emailValue });
  };

  handleCustomerSelect = async (id: string): Promise<void> => {
    const selectedCustomer =
      this.state.customers.length > 0
        ? this.state.customers.find(
          (customer: Customer): boolean => customer.id === id,
        )
        : this.props.customer;

    this.setState(
      {
        selectedCustomer,
        selectedCompany: undefined,
        customerSubscriptions: selectedCustomer?.customerSubscriptions,
        customerPayoutAccounts: selectedCustomer?.customerPayoutAccounts,
        isOlderThanSixty: selectedCustomer?.age !== undefined && selectedCustomer.age >= 60,
      },
      () => {
        this.mapProductsToSubscriptions(this.state.customerSubscriptions);
        this.searchRedemptionRequests();
      },
    );
  };

  mapProductsToSubscriptions = (
    subscriptions: Subscription[] | undefined,
  ): void => {
    const productsWithSubscriptionId: ProductWithSubscriptionId[] = [];
    const products: Product[] = [];
    filterActiveSubscriptions(subscriptions).map((subs: Subscription) => {
      productsWithSubscriptionId.push({
        ...subs.product,
        subscriptionId: subs.id,
        productDuration: subs.productDuration,
        customerPayoutAccounts: subs.customerPayoutAccounts,
      });
      products.push({ ...subs.product });
    });
    this.setState({ products, productsWithSubscriptionId });
  };

  handleCompanySelect = async (id: string): Promise<void> => {
    const selectedCompany =
      this.state.companies.length > 0
        ? this.state.companies.find(
          (company: Company): boolean => company.id === id,
        )
        : [
          ...(this.state.selectedCustomer?.employers ?? []),
          ...(this.state.selectedCustomer?.formerCompanies ?? []),
        ]?.find((company: Company): boolean => company.id === id);

    this.setState({
      selectedCompany,
      // selectedCustomer: undefined,
    });
  };

  handleRequestTypeSelect = async (id: string): Promise<void> => {
    this.setState({
      initialRequestType: id,
      initialTotalAmountType: id === "1" ? "net" : undefined,
    });
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    await this.triggerCustomerSearch();
  }, DEBOUNCE_TIME);

  handleContactTypeChange = (contactType: string): void => {
    let contactTypeFieldsToHide: string[] = [];

    switch (contactType) {
      case "phone":
        contactTypeFieldsToHide = ["email"];
        break;
      case "email":
        contactTypeFieldsToHide = ["phone"];
        break;
      default:
        contactTypeFieldsToHide = [];
        break;
    }

    this.setState({
      contactType,
      contactTypeFieldsToHide,
    });
  };

  getPrimaryPhone = (): string | undefined => {
    let phones: AssociatedPhone[] = [];

    if (this.state.selectedCustomer) {
      phones = this.state.selectedCustomer.activeCustomerPhones;
    }

    if (this.state.selectedCompany) {
      phones = this.state.selectedCompany.companyPhones;
    }

    const phone = phones?.find((p: AssociatedPhone): boolean => p.isPrimary)
      ?.phone.number;

    return phone;
  };

  getPrimaryEmail = (): string | undefined => {
    let emails: AssociatedEmail[] = [];

    if (this.state.selectedCustomer) {
      emails = this.state.selectedCustomer.customerEmails;
    }

    if (this.state.selectedCompany) {
      emails = this.state.selectedCompany.companyEmails;
    }

    const email = emails?.find((e: AssociatedEmail): boolean => e.isPrimary)
      ?.email.address;
    this.reference.current?.setFieldsValue({ email });

    return email;
  };

  triggerCustomerSearch = async (): Promise<void> => {
    const { searchTerm: q } = this.state;
    interface Response {
      count: number;
      results: Customer[];
    }

    this.setState({ isSearching: true });

    try {
      const r: AxiosResponse = await request({
        query: SEARCH_CUSTOMER_MORE_INFO,
        variables: { q, page: 1 },
      });

      const { err, mutation } = responseHandler<Response>(r, "customerSearch");

      if (err) {
        message.error(`customer search failed - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.setState({
          customers: mutation.results,
        });
      }
    } catch (error) {
      message.error(`customer search failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }

    this.setState({ isSearching: false });
  };

  setInitialSelectedCustomerOrCompany = (): void => {
    if (this.props.company) {
      this.setState({ selectedCompany: this.props.company });
    } else {
      this.setState({
        selectedCustomer: this.props.customer,
        customerPayoutAccounts: this.props.customer?.customerPayoutAccounts,
        customerSubscriptions: this.props.customer?.customerSubscriptions,
        isOlderThanSixty: this.props.customer?.age !== undefined && this.props.customer.age >= 60,
      });
      this.mapProductsToSubscriptions(
        this.props.customer?.customerSubscriptions,
      );
    }
  };

  toggleRedemptionBtnVisible = (values: CheckboxValueType[]): void => {
    const { redemptionRequestChecklist } = this.state;
    const checklist = values as string[];
    const requiredFields = redemptionRequestChecklist.filter(
      (checklist) => checklist.isRequired,
    );
    const isAllChecked = requiredFields.every((field) => {
      return checklist.includes(field.name);
    });
    this.setState({
      iscreateRedemptionBtnDisabled: !isAllChecked,
      selectedChecklist: checklist,
    });
  };

  searchRedemptionRequests = async (): Promise<void> => {
    const { selectedCustomer } = this.state;
    interface Response {
      count: number;
      results: RedemptionsRequest[];
    }

    try {
      const r: AxiosResponse = await request({
        query: SEARCH_REDEMPTION_REQUEST_FOR_EDIT,
        variables: { q: selectedCustomer?.petraId, page: 1 },
      });

      const { handled, mutation } = responseHandler<Response>(
        r,
        "redemptionRequestSearch",
      );

      if (handled) {
        message.error("Failed To Load Redemptions");
      } else {
        this.setState({
          isLoading: false,
          redemptionsRequests: mutation.results,
        });
      }
    } catch (error) {
      this.setState({
        isLoading: false,
      });

      message.error(`Search Failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }

    this.setState({ isLoading: false });
  };

  resolveVerificationDetails = async (): Promise<
    { financialInstitutionId: string; accountNumber: string } | undefined
  > => {
    const saved = this.getSelectedSavedPayoutAccount();

    if (saved) {
      return {
        financialInstitutionId:
          (saved.payoutAccount?.bankBranch ?? saved.bankBranch)
            ?.financialInstitution?.id ?? "",
        accountNumber: saved.accountNumber,
      };
    }

    if (!this.reference.current) return undefined;

    try {
      return await this.reference.current.validateFields([
        "financialInstitutionId",
        "accountNumber",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return undefined;
    }
  };

  verifyAccount = async (): Promise<void> => {
    interface Response {
      data: {
        validatePayoutAccountFuzzyMatch: ValidateAccount;
      };
    }

    const data = await this.resolveVerificationDetails();
    if (!data) return;

    this.setState({ isLoading: true });

    try {
      const result = await request({
        query: VALIDATE_PAYOUT_ACCOUNT_FUZZY_MATCH,
        variables: { ...data, customerId: this.state.selectedCustomer?.id },
      });

      const response = (result as AxiosResponse<Response>).data.data
        .validatePayoutAccountFuzzyMatch;
      const { status, score, name } = response;

      if (!status) {
        this.setState({
          accountValidationResponse: undefined,
          lastVerificationFailure: undefined,
        });
        message.error(response.message || "Error verifying account number");
        return;
      }

      if (score >= PAYOUT_VALIDATION_THRESHOLD || this.state.bypassValidation) {
        this.setState({
          accountValidationResponse: response,
          lastVerificationFailure: undefined,
        });
        message.success("Account verified successfully");
        message.success(
          `Account name: ${name} (Match Score: ${Math.round(score)}%)`,
        );
        await this.persistPayoutAccountVerification(score);
        return;
      }

      this.setState({
        accountValidationResponse: undefined,
        lastVerificationFailure: { bankAccountName: name, score },
      });
      message.error(
        `Customer name and Bank account name match too low: ${Math.round(
          score,
        )}%. Must be above ${PAYOUT_VALIDATION_THRESHOLD}.`,
      );
      message.success(`Bank account name: ${name}`);
    } catch (error) {
      message.error(
        `Error verifying account number - ${generateDisplayErrorMessage(error)}`,
      );
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  persistPayoutAccountVerification = async (score: number): Promise<void> => {
    const account = this.getSelectedSavedPayoutAccount();
    const { selectedCustomer, selectedProduct } = this.state;

    if (!account) return;
    if (account.isPayoutAccountVerified) return;
    if (score < PAYOUT_VALIDATION_THRESHOLD) return;
    if (!selectedCustomer?.id || !selectedProduct?.subscriptionId) return;

    try {
      await request({
        query: UPDATE_PAYOUT_ACCOUNT,
        variables: {
          id: account.id,
          accountNumber: account.accountNumber,
          accountType: account.accountType,
          name: account.name,
          bankBranchId: (account.payoutAccount?.bankBranch ?? account.bankBranch)
            ?.id,
          customerId: selectedCustomer.id,
          customerSubscriptionId: selectedProduct.subscriptionId,
          isPayoutAccountVerified: true,
        },
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  createRedemptionRequest = async (): Promise<void> => {
    const {
      accountNumber,
      accountValidationResponse,
      accountName,
      taxWaiverNpraUrl,
      attachmentIds,
      paymentAccountType,
      bankBranchId,
      isFormerCompany,
      contactType,
      selectedProduct,
      savePayoutAccount,
      selectedChecklist,
      initiator
    } = this.state;

    interface Response {
      redemptionRequest: RedemptionsRequest;
    }

    if (!this.reference.current) return;
    if (!this.attachmentsReference.current) return;

    const verificationError = getPayoutVerificationError({
      selectedAccount: this.getSelectedSavedPayoutAccount(),
      hasPassingValidation: !!this.state.accountValidationResponse,
      lastVerificationFailure: this.state.lastVerificationFailure,
      bypassValidation: this.state.bypassValidation,
      clientName: generateNameFromCustomer(this.state.selectedCustomer),
      threshold: PAYOUT_VALIDATION_THRESHOLD,
    });

    if (verificationError) {
      message.error(verificationError);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const modifiedFieldsToBeValidated = paymentAccountType === "cheque"
        ? fieldsToBeValidated.filter((item) => item !== "bankBranchId")
        : fieldsToBeValidated;

      const data = await this.reference.current.validateFields(modifiedFieldsToBeValidated);

      await this.attachmentsReference.current.validateFields(["attachmentUrl"]);

      if (bankBranchId) {
        data["bankBranchId"] = bankBranchId;
      }

      const variables = {
        ...data,
        isFormerCompany,
        contactInformation: data[contactType],
        customerSubscriptionId: selectedProduct?.subscriptionId,
        savePayoutAccount,
        waiveOffTax: data.waiveOffTax === "yes",
        newPayoutAccount: data.payoutAccountId === "new_account",
        payoutAccountId: data.payoutAccountId === "new_account" ? "" : data.payoutAccountId,
        checklist: selectedChecklist,
        accountNumber,
        name: accountValidationResponse?.name || accountName,
        idTypeId: "5", //Ghana Card
        taxWaiverNpraUrl,
        attachmentIds,
        // "Override Vesting Rule?" option temporarily removed from the UI for Tier 3 redemptions - default to "No"
        includeVestingRule: false,
        vestingValue: undefined,
        authorizerCompanyContactId: data.authorizerCompanyContactId,
        initiator
      };

      const response: AxiosResponse = await request({
        query: CREATE_REDEMPTION_REQUEST,
        variables,
      });

      const { err, mutation } = responseHandler<Response>(response, "createRedemptionRequest");

      console.log(err);

      if (err) {
        displayRedemptionError(err);
      } else {
        this.reference.current.resetFields();
        this.attachmentsReference.current.resetFields();
        message.success("Redemption request created successfully");

        if (!this.props.customer) {
          this.redirectToViewRedemptionPage(mutation.redemptionRequest.id);
        }

        if (this.props.handleSuccess) {
          this.props.handleSuccess();
        }
      }
    } catch (error) {
      message.error(`Failed to create redemption request - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false, showConfirmCreateModal: false });
    }
  };

  createAttachment = async (combinedInfo: string): Promise<void> => {
    interface Response {
      attachment: Attachment;
    }

    const fieldsToBeValidated: string[] = ["url"];

    this.setState({ isLoading: true });

    try {
      const [url, name] = combinedInfo.split("@@@");

      const response: AxiosResponse = await request({
        query: CREATE_ATTACHMENT,
        variables: {
          url,
          name,
        },
      });

      const { mutation } = responseHandler<Response>(
        response,
        "createAttachment",
      );
      this.setState((state) => ({
        attachmentIds: [...state.attachmentIds, mutation.attachment.id],
        attachments: [...state.attachments, mutation.attachment],
      }));
      this.reference.current &&
        this.reference.current.resetFields(fieldsToBeValidated);
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  removeAttachment = (attachmentId: string): void => {
    this.setState((state) => ({
      attachments: state.attachments.filter(
        (attachment) => attachment.id !== attachmentId,
      ),
      attachmentIds: state.attachmentIds.filter((id) => id !== attachmentId),
    }));
  };

  handleRedemptionTypeChange = (redemptionType: string): void => {
    let type = "";

    switch (redemptionType) {
      case "percentage":
        type = "Percentage";
        break;
      case "amount":
        type = "Amount";
        break;
      default:
        break;
    }

    this.setState({
      redemptionType: type,
    });
  };

  handleProductSelect = async (id: string): Promise<void> => {
    const { customerContributions, selectedCustomer, isOlderThanSixty } = this.state;
    let contributedLongerThanTenYears = false;
    const product = this.state.productsWithSubscriptionId?.find(
      (product) => product?.id === id,
    );

    if (product) {
      const isTier3Company =
        product?.productTier?.name === "tier_3" &&
        product?.opportunityProspect?.name === "Company";

      this.setState({
        vestingRuleSectionFieldsToHide: isTier3Company ? [] : ["includeVestingRule"],
        authorizerFieldsToHide: isTier3Company ? [] : ["authorizerCompanyContactId"],
      });

      const productContribution = customerContributions?.find(
        (contribution: ProductContribution) =>
          contribution.fundcode === product?.fundCode,
      );

      if (selectedCustomer && product.id && !isOlderThanSixty) {
        contributedLongerThanTenYears = isProductDurationTenYearsOrMore(product.productDuration || "");
      }

      this.setState({ selectedProduct: product, productContribution, contributedLongerThanTenYears }, () => {
        let customerModifiedAccounts: SelectOptionsInterface[] = [];
        if (
          this.state.selectedProduct?.customerPayoutAccounts &&
          this.state.selectedProduct?.customerPayoutAccounts.length > 0
        ) {
          customerModifiedAccounts = formatPayoutAccountAsSelectOptions(
            this.state.selectedProduct?.customerPayoutAccounts,
          );
        } else {
          customerModifiedAccounts = [
            { id: "new_account", name: "New Redemption Payout Account" },
          ];
        }
        this.getRedemptionPurposes()
        this.setState({
          customerModifiedAccounts
        });
      });
    }
  };

  handlePaymentModeSelect = (mode: string) => {
    const paymentMode = mode as PaymentModeTypesEnum;
    const filteredAccounts = filterAccountsByMode(this.state.selectedProduct?.customerPayoutAccounts ?? [], paymentMode);
    const customerModifiedAccounts = formatPayoutAccountAsSelectOptions(filteredAccounts);

    if (paymentMode === "cheque") {
      this.setState({ isCheque: true })
    } else this.setState({ isCheque: false })

    this.setState({ customerModifiedAccounts, paymentMode });
  }

  getSelectedSavedPayoutAccount = (): PaymentAccount | undefined => {
    const { selectedPayoutAccount, selectedProduct } = this.state;
    if (!selectedPayoutAccount || selectedPayoutAccount === "new_account") {
      return undefined;
    }
    return (selectedProduct?.customerPayoutAccounts ?? []).find(
      (account) =>
        account.payoutAccount?.id === selectedPayoutAccount ||
        account.id === selectedPayoutAccount,
    );
  };

  handlePayoutAccountSelect = (id: string): void => {
    let payoutAccountFieldsToHide: string[] = [];
    let creatingNewPayoutAccount = false;

    if (id === "new_account") {
      payoutAccountFieldsToHide = ["name", "bankBranchId"];
      creatingNewPayoutAccount = true;
    } else {
      const account = (this.state.selectedProduct?.customerPayoutAccounts ?? []).find(
        (item) => item.payoutAccount?.id === id || item.id === id,
      );
      payoutAccountFieldsToHide = savedPayoutAccountHiddenFields(account);
      creatingNewPayoutAccount = false;
    }

    this.setState({
      payoutAccountFieldsToHide,
      creatingNewPayoutAccount,
      selectedPayoutAccount: id,
      accountName: "",
      accountNumber: "",
      accountValidationResponse: undefined,
      lastVerificationFailure: undefined,
    });
  };

  handlePaymentTypeSelect = (paymentAccountType: string): void => {
    let financialInstitutions: FinancialInstitution[] = [];
    let payoutAccountFieldsToHide: string[] = this.state.payoutAccountFieldsToHide.slice()
    if (paymentAccountType === "mm_account") {
      financialInstitutions = this.state.financialInstitutions.filter(
        (institution: FinancialInstitution): boolean => {
          return institution.institutionType === "mobile_money";
        },
      );
    } else {
      financialInstitutions = this.state.financialInstitutions.filter(
        (institution: FinancialInstitution): boolean => {
          return institution.institutionType === "bank";
        },
      );

    }
    if (paymentAccountType === "cheque") {
      payoutAccountFieldsToHide = payoutAccountFieldsToHide.filter(field => field !== "name");
      payoutAccountFieldsToHide.push("bankBranchId");
    } else {
      payoutAccountFieldsToHide = payoutAccountFieldsToHide.filter(field => field !== "bankBranchId")
      payoutAccountFieldsToHide.push("name");
    }

    this.setState({
      institutionOptions: financialInstitutions,
      payoutAccountFieldsToHide,
    });
  };

  handlePaymentTypeChange = (): void => {
    this.setState({ accountNumber: "", accountValidationResponse: undefined, payoutFinancialInstitutionId: "" });
  };

  handleAccountNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ accountName: event.target.value });
  };

  handleFileUploadChange = (field: string, combinedInfo: string): void => {
    const [url] = combinedInfo.split("@@@");
    this.setState((prevState) => ({
      ...prevState,
      [field]: url
    }));
  }

  handleRedemptionPurposeChange = async (id: string) => {
    let newRedemptionFieldsToHide = this.state.redemptionFieldsToHide;

    return Promise.all([
      this.getRedemptionChecklist(id),
      this.getRedemptionRequestSubPurposes(id),
    ]).then(() => {
      if (id === otherRedemptionPurposeId.toString()) {
        newRedemptionFieldsToHide = this.state.redemptionFieldsToHide.filter(
          (field) => field !== "additionalPurposeDescription",
        );
      } else {
        newRedemptionFieldsToHide = [
          ...this.state.redemptionFieldsToHide,
          "additionalPurposeDescription",
        ];
      }

      this.setState({
        redemptionFieldsToHide: newRedemptionFieldsToHide,
      });
    });
  };

  getRedemptionChecklist = async (id: string): Promise<void> => {
    const { selectedProduct } = this.state;
    interface Response {
      data: {
        redemptionRequestChecklist: RedemptionRequestChecklist[];
      };
    }

    this.setState({ isSearching: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: GET_REDEMPTION_CHECKLIST,
        variables: {
          productId: selectedProduct?.id,
          redemptionRequestPurposeId: id,
        },
      });

      this.setState({
        redemptionRequestChecklist:
          response.data.data.redemptionRequestChecklist,
        selectedChecklist: [],
        iscreateRedemptionBtnDisabled: true,
      });
    } catch (error) {
      Sentry.captureException(error);
    }

    this.setState({ isSearching: false });
  };

  getRedemptionRequestSubPurposes = async (id: string) => {
    const { selectedProduct } = this.state;
    interface Response {
      data: {
        redemptionRequestSubPurposes: SelectOptionsInterface[];
      };
    }

    this.setState({ isSearching: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: GET_REDEMPTION_SUB_PURPOSES,
        variables: {
          productId: selectedProduct?.id,
          redemptionRequestPurposeId: id,
        },
      });

      const data = response.data.data.redemptionRequestSubPurposes;
      const fields = data.length > 0 ? [] : ["redemptionRequestSubPurposeId"];

      this.setState({
        redemptionRequestSubPurposes: data,
        redemptionFieldsToHide: fields,
      });
    } catch (error) {
      Sentry.captureException(error);
    }

    this.setState({ isSearching: false });
  };

  generateRedemptionText() {
    const redemptionType = this.state.redemptionType;
    if (redemptionType === "") return "Redemption Value";
    return redemptionType === "Amount"
      ? "Redemption Amount"
      : "Redemption Percentage";
  }

  handleVestingRuleChange = (value: string) => {
    if (value === "yes") {
      this.setState({ vestingRuleFieldsToHide: [] });
    } else {
      this.setState({ vestingRuleFieldsToHide: ["vestingValue"] });
    }
  };

  generateBranchMap = async (): Promise<void> => {
    const branchMap: BranchMap = {};

    this.state.financialInstitutions.forEach(
      (institution: FinancialInstitution): void => {
        branchMap[institution.id] = institution.bankBranches;
      },
    );

    this.setState({ branchMap });
  };

  handlePayoutFinancialInstitutionChange = async (
    payoutFinancialInstitutionId: string,
  ): Promise<void> => {
    this.setState({
      payoutFinancialInstitutionId,
      bankBranchId: this.state.branchMap[payoutFinancialInstitutionId][0].id,
      payoutBranches: this.state.branchMap[payoutFinancialInstitutionId]
    });
  };

  handleBypassValidationChange = async (checkedValue: boolean) => {
    this.setState({ bypassValidation: checkedValue });
  };

  handleAccountTypeChange = (): void => {
    this.setState({
      accountNumber: "",
      accountValidationResponse: undefined,
      payoutFinancialInstitutionId: "",
    });
  };

  getFinancialInstitutionals = async (): Promise<void> => {
    interface Response {
      data: {
        financialInstitutions: FinancialInstitution[];
      };
    }
    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_FINANCIAL_INSTITUTIONS,
      });

      this.setState({
        financialInstitutions: data.financialInstitutions,
        isLoading: false,
      });

      this.generateBranchMap();
    } catch (error) {
      Sentry.captureException(error);
    }
    this.setState({ isLoading: false });
  };

  getIdTypes = async (): Promise<void> => {
    interface Response {
      data: {
        idTypes: Id[];
      };
    }

    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_ID_TYPES,
      });

      this.setState({
        idTypes: data.idTypes,
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  getDepartments = async (): Promise<Department[]> => {
    interface Response {
      data: {
        departments: Department[];
      };
    }

    try {
      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_DEPARTMENTS,
      });

      return data.departments;
    } catch (error) {
      Sentry.captureException(error);
    }
    return [];
  };

  getAllUsers = (): void => {
    request({
      query: ALL_USERS_QUERY,
      variables: {
        page: 1,
        q: this.state.userSearchTerm,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        users: data.users.results,
        isLoading: false,
      });
    });
  };

  getCategories = async (): Promise<void> => {
    interface Response {
      data: {
        categories: Category[];
      };
    }

    try {
      this.setState({ isLoading: true });

      const departments = await this.getDepartments();

      if (departments.length > 0) {
        const clDepartmet = departments.find(
          (department: Department): boolean => {
            return (
              department.name.toLowerCase() === "client services" ||
              department.name.toLowerCase() === "client service"
            );
          },
        );

        const {
          data: { data },
        }: AxiosResponse<Response> = await request({
          query: GET_CATEGORIES,
          variables: {
            departmentId: clDepartmet?.id,
          },
        });
        const category = data.categories.find((category: Category): boolean => {
          return category.name.toLowerCase() === "redemptions and transfers";
        });
        this.setState({
          category: category,
        });
      }
    } catch (error) {
      Sentry.captureException(error);
    }
    this.setState({ isLoading: false });
  };

  handleTaxWaiveChange = async (value: string) => {
    this.setState({
      taxWaiverFieldsToHide: value === "yes" ? [] : ["taxWaiverNpraUrl"],
    });

  };

  renderLabel(required: boolean, name: string) {
    return (
      <label>
        {required ? (
          <span>
            <span style={{ color: "red" }}>*</span> {name}
          </span>
        ) : (
          name
        )}
      </label>
    );
  }

  toggleCancelModal = async () => {
    this.setState((prevState) => ({
      showCancelRequest: !prevState.showCancelRequest,
    }));
  };

  cancelRequest = async () => {
    const backLocation = window.location.pathname.replace("/new", "");
    window.location.assign(backLocation);
  };

  changeIsFormerCompany = async (checkedValue: boolean) => {
    this.setState(
      {
        isFormerCompany: checkedValue,
      },
      () => {
        this.reference.current &&
          this.reference.current.setFieldsValue({ companyId: undefined });
      },
    );
  };

  handleRequestorTypeChange = (initiator: string): void => {
    let requestorTypeFieldsToHide: string[] = [];

    if (initiator === "customer") {
      requestorTypeFieldsToHide = ["clientContactInformation"]
    }

    this.setState({
      initiator,
      requestorTypeFieldsToHide
    });
  };

  render(): JSX.Element {
    const {
      isLoading,
      products,
      isSearching,
      customers,
      selectedCustomer,
      selectedCompany,
      contactTypeFieldsToHide,
      vestingRuleSectionFieldsToHide,
      redemptionFieldsToHide,
      redemptionRequestChecklist,
      iscreateRedemptionBtnDisabled,
      selectedChecklist,
      redemptionPurposes,
      redemptionRequestSubPurposes,
      showConfirmCreateModal,
      vestingRuleFieldsToHide,
      authorizerFieldsToHide,
      taxWaiverFieldsToHide,
      payoutAccountFieldsToHide,
      payoutBranches,
      selectedProduct,
      category,
      users,
      customerSubscriptions,
      isFormerCompany,
      savePayoutAccount,
      paymentAccountType,
      accountValidationResponse,
      customerModifiedAccounts,
      financialInstitutions,
      isCheque,
      accountName,
      initiator,
      requestorTypeFieldsToHide,
      bypassValidation,
      contributedLongerThanTenYears,
      idTypes,
      isOlderThanSixty
    } = this.state;

    const { company: propCompany } = this.props;

    const waiveOffTaxAutomatically = isOlderThanSixty || contributedLongerThanTenYears;

    const allCustomerLiens =
      customerSubscriptions
        ?.map((sub) => {
          return sub?.customerLiens;
        })
        ?.reduce(
          (previousValue: CustomerLien[], currentValue: CustomerLien[]) => {
            return previousValue.concat(currentValue);
          },
          [],
        ) ?? [];

    const modifiedProducts: Product[] = injectKey<Product>(products, "name");
    let customerAssociatedCompanies: SelectOptionsInterface[] =
      selectedCustomer?.employers?.map((associatedCompany) => {
        return {
          id: associatedCompany.id,
          name: associatedCompany.name,
        };
      }) || [];

    let customerAssociatedFormerCompanies: SelectOptionsInterface[] =
      selectedCustomer?.formerCompanies?.map((associatedCompany) => {
        return {
          id: associatedCompany.id,
          name: associatedCompany.name,
        };
      }) || [];

    if (propCompany) {
      customerAssociatedCompanies = [
        ...customerAssociatedCompanies,
        { id: propCompany.id, name: propCompany.name },
      ];
      customerAssociatedFormerCompanies = [
        ...customerAssociatedFormerCompanies,
        { id: propCompany.id, name: propCompany.name },
      ];
    }

    const config: FormFieldConfig[] = [
      {
        id: "initiator",
        name: "initiator",
        inputType: "select",
        label: "Requestor Type",
        rule: "string",
        placeholder: "Requestor Type",
        changeHandler: this.handleRequestorTypeChange,
        disabled: !!(this.props.customer || this.props.company),
        required: true,
        value: initiator,
        initialValue: initiator,
        colConfig: {
          span: 24,
        },
        selectOptions: [
          { id: "customer", name: "Customer" },
          { id: "company", name: "Company" },
        ],
      },
      {
        id: "customerId",
        name: "customerId",
        inputType: "search",
        required: true,
        placeholder: "Seach by name or petraId",
        rule: "string",
        label: "Customer",
        selectOptions: this.props.customer
          ? convertCustomersToSelectOptions([this.props.customer])
          : convertCustomersToSelectOptions(customers),
        changeHandler: this.handleSearchInput,
        selectHandler: this.handleCustomerSelect,
        initialValue: this.props.customer
          ? this.props.customer?.id
          : selectedCustomer?.id,
        isSearching,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "isFormerCompany",
        name: "isFormerCompany",
        required: false,
        inputType: "checkbox",
        rule: "boolean",
        label: "Is Former Company ?",
        checkboxCheckedValue: isFormerCompany,
        checkboxChangeHandler: (e) =>
          this.changeIsFormerCompany(e.target.checked),
        elementStyle: { width: "100%" },
        colConfig: {
          span: 24,
        },
      },
      {
        id: "companyId",
        name: "companyId",
        inputType: "select",
        required: true,
        placeholder: `Select customer associated ${isFormerCompany ? "former" : ""
          } company`,
        rule: "string",
        label: "Company",
        selectOptions: isFormerCompany
          ? customerAssociatedFormerCompanies
          : customerAssociatedCompanies,
        selectHandler: this.handleCompanySelect,
        changeHandler: this.handleCompanySelect,
        initialValue: this.state.selectedCompany?.id,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "subCategoryId",
        name: "subCategoryId",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Request Type",
        placeholder: "Request Type",
        selectOptions: category?.subCategories,
        selectHandler: this.handleRequestTypeSelect,
        changeHandler: this.handleRequestTypeSelect,
        initialValue: this.state.initialRequestType,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "userId",
        name: "userId",
        rule: "string",
        inputType: "search",
        required: true,
        label: "Assigned To",
        placeholder: "Assigned To",
        selectOptions: users,
        changeHandler: this.handleUserSearchInput,
        isSearching,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "redemptionType",
        name: "redemptionType",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Redemption Type",
        placeholder: "Redemption Type",
        value: this.state.redemptionType,
        changeHandler: this.handleRedemptionTypeChange,
        selectOptions: generateRedemptionTypes(),
        colConfig: {
          span: 24,
        },
      },
      {
        name: "redemptionValue",
        rule: "number",
        inputType: "number",
        required: true,
        label: this.generateRedemptionText(),
        placeholder: this.generateRedemptionText(),
        elementStyle: { width: "100%" },
        colConfig: {
          span: 24,
        },
      },
      {
        id: "totalAmountType",
        name: "totalAmountType",
        inputType: "select",
        rule: "string",
        label: "Select Redemption Total Amount Type",
        placeholder: "Select Redemption Total Amount Type",
        required: true,
        disabled: this.state.initialTotalAmountType !== undefined,
        selectOptions: [
          { id: "gross", name: "Gross" },
          { id: "net", name: "Net" },
        ],
        initialValue: this.state.initialTotalAmountType,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "customerSubscriptionId",
        name: "customerSubscriptionId",
        inputType: "select",
        rule: "string",
        label: "Product",
        placeholder: "Select Product",
        required: true,
        changeHandler: this.handleProductSelect,
        selectHandler: this.handleProductSelect,
        selectOptions: modifiedProducts,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "paymentMode",
        name: "paymentMode",
        placeholder: "Payment Mode",
        inputType: "select",
        rule: "string",
        label: "Payment Mode",
        selectHandler: this.handlePaymentModeSelect,
        selectOptions: [
          {
            id: "",
            name: "N/A",
            value: "",
          },
          {
            id: PaymentModeTypesEnum.cheque,
            name: paymentModeTypes.cheque,
            value: PaymentModeTypesEnum.cheque,
          },
          {
            id: PaymentModeTypesEnum.bankTransfer,
            name: paymentModeTypes.bank_transfer,
            value: PaymentModeTypesEnum.bankTransfer,
          },
        ],
        required: false,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "payoutAccountId",
        name: "payoutAccountId",
        inputType: "select",
        rule: "string",
        label: "Payout Account",
        placeholder: "Select Payout Account",
        required: false,
        selectOptions: customerModifiedAccounts,
        changeHandler: this.handlePayoutAccountSelect,
        selectHandler: this.handlePayoutAccountSelect,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "accountType",
        name: "accountType",
        placeholder: "Type",
        inputType: "select",
        rule: "string",
        selectHandler: this.handlePaymentTypeSelect,
        changeHandler: this.handlePaymentTypeChange,
        label: "Payment Account Type",
        initialValue: paymentAccountType,
        selectOptions: [
          {
            id: PaymentAccountTypesEnum.cheque,
            name: paymentAccountTypes.cheque,
            value: PaymentAccountTypesEnum.cheque,
          },
          {
            id: PaymentAccountTypesEnum.bankAccount,
            name: paymentAccountTypes.bank_account,
            value: PaymentAccountTypesEnum.bankAccount,
          },
        ],
        required: false,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "financialInstitutionId",
        name: "financialInstitutionId",
        placeholder: "Institution Name",
        inputType: "select",
        selectHandler: this.handlePayoutFinancialInstitutionChange,
        selectOptions: financialInstitutions,
        rule: "string",
        label: "Institution Name",
        required: !isCheque,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "bankBranchId",
        name: "bankBranchId",
        placeholder: "Branch",
        inputType: "select",
        rule: "string",
        label: "Branch",
        selectOptions: payoutBranches,
        required: true,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "accountNumber",
        name: "accountNumber",
        placeholder: "Account Number",
        inputType: "string",
        rule: "string",
        label: "Account Number",
        onChange: this.handleAccountNumberChange,
        required: !isCheque,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "bypassValidation",
        name: "bypassValidation",
        inputType: "checkbox",
        required: false,
        rule: "boolean",
        label: "Third Party?",
        checkboxCheckedValue: bypassValidation,
        checkboxChangeHandler: (e) => this.handleBypassValidationChange(e.target.checked),
        elementStyle: { width: "100%" },
        colConfig: {
          span: 24,
        },
      },
      {
        id: "accountName",
        name: "accountName",
        inputType: isCheque ? "string" : "description",
        rule: "string",
        required: true,
        placeholder: isCheque ? "Account Name" : "",
        label: "Account Name",
        onChange: this.handleAccountNameChange,
        value: accountValidationResponse?.name ?? accountName,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "verifyPayoutAccount",
        name: "verifyPayoutAccount",
        inputType: "button",
        className: "base-button",
        required: false,
        handleClick: this.verifyAccount,
        value: "Verify",
        elementStyle: {
          position: "relative",
          textAlign: "left",
        },
        colConfig: {
          span: 24,
        },
      },
      {
        id: "redemptionRequestPurposeId",
        name: "redemptionRequestPurposeId",
        inputType: "select",
        rule: "string",
        label: "Select Redemption Purpose",
        placeholder: "Select Redemption Purpose",
        required: true,
        changeHandler: this.handleRedemptionPurposeChange,
        selectOptions: redemptionPurposes,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "savePayoutAccount",
        name: "savePayoutAccount",
        rule: "boolean",
        inputType: "checkbox",
        required: false,
        label: "Save Payout Account?",
        checkboxCheckedValue: savePayoutAccount,
        disabled: bypassValidation,
        checkboxChangeHandler: (e) =>
          this.toggleSavePayoutAccount(e.target.checked),
        colConfig: {
          span: 7,
        },
      },
      {
        name: "contactType",
        rule: "string",
        inputType: "select",
        required: false,
        label: "Contact Type",
        placeholder: "Contact Type",
        changeHandler: this.handleContactTypeChange,
        selectOptions: generateContactTypes(),
        colConfig: {
          span: 24,
        },
      },
      {
        id: "email",
        name: "email",
        inputType: "string",
        rule: "email",
        label: "Redemption notification sent to",
        placeholder: "Enter email address ...",
        required: false,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "phone",
        name: "phone",
        inputType: "phone",
        rule: "string",
        label: "Redemption notification sent to",
        placeholder: "Enter phone number",
        value: this.getPrimaryPhone(),
        required: false,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "clientContactInformation",
        name: "clientContactInformation",
        inputType: "string",
        rule: "email",
        label: "Client redemption notification sent to (Email)",
        placeholder: "Enter email address...",
        required: initiator === "company",
        colConfig: {
          span: 24,
        },
      },
      {
        id: "additionalPurposeDescription",
        name: "additionalPurposeDescription",
        inputType: "string",
        rule: "string",
        label: "Purpose Description",
        placeholder: "Purpose description",
        required: true,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "redemptionRequestSubPurposeId",
        name: "redemptionRequestSubPurposeId",
        inputType: "select",
        rule: "string",
        label: "Select Redemption Sub Purpose",
        placeholder: "Select Redemption Sub Purpose",
        required: false,
        selectOptions: redemptionRequestSubPurposes,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "idTypeId",
        name: "idTypeId",
        inputType: "select",
        rule: "string",
        required: true,
        disabled: true,
        placeholder: "Ghana Card",
        selectOptions: idTypes,
        label: "ID Type",
        defaultValue: "5",
        colConfig: {
          span: 24,
        },
      },
      {
        id: "idNumber",
        name: "idNumber",
        inputType: "string",
        rule: "string",
        required: true,
        disabled: false,
        placeholder: "Ghana Card Number",
        label: "Ghana Card Number",
        pattern: ghanaCardRegex,
        patternErrorMessage: "Invalid Ghana Card Number format. Expected format: ABC-123456789-1",
        colConfig: {
          span: 24,
        },
      },
      // Temporarily disabled for Tier 3 redemptions - defaults to "No" on the backend (see createRedemptionRequest)
      // {
      //   name: "includeVestingRule",
      //   rule: "string",
      //   inputType: "select",
      //   required: true,
      //   label: "Override Vesting Rule?",
      //   placeholder: "Override Vesting Rule?",
      //   value: "",
      //   changeHandler: this.handleVestingRuleChange,
      //   selectOptions: [
      //     { id: "yes", name: "Yes" },
      //     { id: "no", name: "No" },
      //   ],
      //   colConfig: {
      //     span: 24,
      //   },
      // },
      {
        name: "authorizerCompanyContactId",
        rule: "string",
        inputType: "select",
        // Still offered, but companies that can no longer endorse employment
        // have no authorizer to pick, so we stop blocking on it.
        required: !isAuthorizerOptionalForCompany(selectedCompany),
        label: "Select Authorizer",
        placeholder: "Select Authorizer",
        selectOptions: (selectedCompany?.authorizers ?? []).map((a) => ({
          id: a.id,
          name: a.emailAddress ? `${a.contactName} (${a.emailAddress})` : a.contactName,
        })),
        colConfig: {
          span: 24,
        },
      },
      {
        name: "vestingValue",
        rule: "number",
        inputType: "number",
        required: true,
        label: "Vesting Percentage (0–100)",
        placeholder: "Enter vesting percentage",
        elementStyle: { width: "100%" },
        colConfig: {
          span: 24,
        },
      },
      {
        name: "waiveOffTax",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Should Tax Be Waived Off?",
        placeholder: waiveOffTaxAutomatically ? "Yes" : "Should Tax Be Waived Off?",
        initialValue: waiveOffTaxAutomatically ? "yes" : "no",
        value: waiveOffTaxAutomatically ? "yes" : "no",
        disabled: waiveOffTaxAutomatically,
        changeHandler: this.handleTaxWaiveChange,
        selectOptions: [
          { id: "yes", name: "Yes" },
          { id: "no", name: "No" },
        ],
        colConfig: {
          span: 24,
        },
      },
      {
        name: "taxWaiverNpraUrl",
        label: "Upload Contribution Verification Document",
        inputType: "fileUpload",
        defaultFileList: [],
        placeholder: "Upload Contribution Verification Document",
        rule: "string",
        changeHandler: (combinedInfo: string) => this.handleFileUploadChange("taxWaiverNpraUrl", combinedInfo),
        required: false,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "requestDate",
        name: "requestDate",
        inputType: "defaultDate",
        required: true,
        rule: "object",
        label: "Request Date",
        initialValue: this.getRequestDate(),
        elementStyle: { width: "100%" },
        colConfig: {
          span: 24,
        },
      },
    ];
    const options = redemptionRequestChecklist.map((checklist) => {
      return {
        value: checklist.name,
        label: this.renderLabel(checklist.isRequired, checklist.name),
      };
    });

    return (
      <Fragment>
        {showConfirmCreateModal &&
          ModalCreator({
            title: "Create Redemption Request",
            okType: "primary",
            okText: "Create",
            showModal: showConfirmCreateModal,
            onOkay: this.createRedemptionRequest,
            toggleModal: this.toggleModal,
            inscription:
              "A pending redemption request exists for this customer and product subscription. Are you sure you want to continue?",
          })}
        <Spin spinning={isLoading}>
          <Card header="New Redemption Request">
            <Row>
              <Col span={8}>
                <BaseForm
                  reference={this.reference}
                  fieldsToHide={[
                    ...contactTypeFieldsToHide,
                    ...redemptionFieldsToHide,
                    ...vestingRuleFieldsToHide,
                    ...authorizerFieldsToHide,
                    ...taxWaiverFieldsToHide,
                    ...payoutAccountFieldsToHide,
                    ...vestingRuleSectionFieldsToHide,
                    ...requestorTypeFieldsToHide,
                  ]}
                  layout="vertical"
                  fields={config}
                />
              </Col>

              <Col offset={1} span={15}>
                <AttachmentsSection
                  reference={this.attachmentsReference}
                  createAttachment={this.createAttachment}
                  attachments={this.state.attachments}
                  showForm={true}
                  handleRemove={this.removeAttachment}
                />
                {/* customer lien */}
                {
                  <div>
                    {allCustomerLiens.length > 0 && (
                      <h3>Customer Lien Information</h3>
                    )}
                    {allCustomerLiens &&
                      allCustomerLiens?.map(
                        (lien: CustomerLien, index: number) => {
                          if (!lien.loanPaid) {
                            return (
                              <React.Fragment key={lien.id}>
                                <Descriptions layout={"horizontal"}>
                                  <Descriptions.Item
                                    label={<b>Employee Pre tax</b>}
                                  >
                                    {formatter.format(lien.employeePreTax)}
                                  </Descriptions.Item>
                                  <Descriptions.Item
                                    label={<b>Employee Post tax</b>}
                                  >
                                    {formatter.format(lien.employeePostTax)}
                                  </Descriptions.Item>
                                  <Descriptions.Item
                                    label={<b>Employer Pre tax</b>}
                                  >
                                    {formatter.format(lien.employerPreTax)}
                                  </Descriptions.Item>
                                  <Descriptions.Item
                                    label={<b>Employer Post tax</b>}
                                  >
                                    {formatter.format(lien.employerPostTax)}
                                  </Descriptions.Item>
                                  <Descriptions.Item
                                    label={<b>{formatSnakeCase("Provider")}</b>}
                                  >
                                    {capitalizeFirstLetter(
                                      formatSnakeCase(lien?.provider || ""),
                                    )}
                                  </Descriptions.Item>
                                  <Descriptions.Item
                                    label={
                                      <b>{formatSnakeCase("Total Amount")}</b>
                                    }
                                  >
                                    {formatter.format(lien?.totalAmount)}
                                  </Descriptions.Item>
                                  {/* current amount */}
                                  <Descriptions.Item
                                    label={
                                      <b>{formatSnakeCase("Current Amount")}</b>
                                    }
                                  >
                                    {formatter.format(lien?.currentAmount)}
                                  </Descriptions.Item>
                                </Descriptions>
                              </React.Fragment>
                            );
                          } else {
                            return (
                              <React.Fragment key={lien.id}></React.Fragment>
                            );
                          }
                        },
                      )}
                  </div>
                }
                {allCustomerLiens.length > 0 && <hr />}

                {selectedCustomer && selectedProduct?.fundCode && (
                  <CustomerBalanceBreakdown
                    customerPetraId={selectedCustomer.petraId}
                    fundCode={selectedProduct.fundCode}
                    clientPetraId={selectedCompany?.petraId ?? propCompany?.petraId}
                    showTitle={true}
                    showPendingRedemptionsCard={true}
                    compact={false}
                  />
                )}

                {selectedCustomer && selectedProduct?.fundCode && <hr></hr>}

                {selectedProduct && (
                  <Descriptions
                    layout={"horizontal"}
                    style={{ marginTop: "10px" }}
                  >
                    <Descriptions.Item
                      label={<b>Product Duration( {selectedProduct.name})</b>}
                    >
                      {selectedProduct.productDuration}
                    </Descriptions.Item>
                  </Descriptions>
                )}

                {selectedProduct && <hr></hr>}

                <div>
                  {redemptionRequestChecklist.length > 0 && (
                    <>
                      <h3>Redemption Checklist</h3>
                      <Checkbox.Group
                        options={options}
                        value={selectedChecklist}
                        onChange={this.toggleRedemptionBtnVisible}
                      ></Checkbox.Group>
                    </>
                  )}
                  <div>
                    {redemptionRequestChecklist.length > 0 && (
                      <Button
                        type="primary"
                        disabled={iscreateRedemptionBtnDisabled}
                        onClick={this.toggleConfirmCreateModal}
                        className="base-button"
                        style={{
                          position: "relative",
                          top: "10px",
                          right: "5px",
                          borderRadius: 0,
                          height: "33px",
                        }}
                      >
                        Create Redemption Request
                      </Button>
                    )}
                    <Button
                      type="primary"
                      onClick={this.toggleCancelModal}
                      className="base-button"
                      style={{
                        position: "relative",
                        top: "10px",
                        borderRadius: 0,
                        height: "33px",
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                  {this.state.showCancelRequest &&
                    ModalCreator({
                      title: "Cancel",
                      okType: "primary",
                      okText: "Yes",
                      width: 400,
                      showModal: true,
                      onOkay: this.cancelRequest,
                      toggleModal: this.toggleCancelModal,
                      showFooter: true,
                      inscription: `Are you sure you want to cancel request ?`,
                    })}
                </div>
              </Col>
            </Row>
          </Card>
        </Spin>
      </Fragment>
    );
  }
}
